import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Observable } from "rxjs";
import { DELETE_TYPE } from "src/app/constants";
import { BaseService } from "src/app/services/base.service";
import { ITableState, TableResponseModel } from "src/app/services/crud-table/models/table.model";
import { environment } from "../../../../environments/environment";
import { ProductsCountDTO } from "../../../services/TitleSearch.service";
import { BuyerProductManagementModel } from "../models/buyer-products.model";

export interface UpdateProductModel {
	message: String
};

@Injectable({
	providedIn: 'root'
})

export class BuyerProductsService<T> extends BaseService<T> {
	currentLanguage: string;
	_uploadsLeft$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

	private _products$: BehaviorSubject<any[]> = new BehaviorSubject([]);

    get products$() {
        return this._products$.asObservable();
    }

	set products$(value: any) {
		this._products$.next(value);
	}

	private _productCount$: BehaviorSubject<number> = new BehaviorSubject(0);

    get productCount$() {
        return this._productCount$.asObservable();
    }

	constructor(
		private translateService: TranslateService,
		@Inject(HttpClient) http
	) {
		super(http);
		this.currentLanguage = this.translateService.currentLang;
		this.translateService.onLangChange.subscribe(response => {
			this.currentLanguage = response.lang;
		});
	}

	/**
	 * Handle create address
	 * @param item : Address data object
	 * @returns
	 */
	createProduct(item): Observable<BuyerProductManagementModel> {
		const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.BUYER.PRODUCTS.CREATE;
		return this.create(item, url);
	}

	getProductById(ids: string[]): Observable<any> {
		const body = {
			filter: {
				productId: ids
			},
			paginator: {
				page: 1,
				pageSize: 10,
				total: 10,
				pageSizes: []
			},
			grouping: {
				selectedRowIds: {},
				itemIds: []
			},
			searchTerm: '',
			sorting: {
				column: '_id',
				direction: 'desc'
			}
		};
		const URI = environment.API_ENDPOINT + environment.VERSION_V1 + environment.API_ROUTES.BUYER.PRODUCTS.SEARCH_SHOP;
		// return this.post(URI, body);
		return this.http.post<TableResponseModel<T>>(URI, body);
	}

	/**
	 *	Find by search string
	 * @param tableState :table state parameters
	 * @returns
	 */
	findMyProducts(tableState: ITableState) {
		let url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.BUYER.PRODUCTS.SEARCH;
		this.patchState(url, tableState).subscribe((res:TableResponseModel<T>)=> {
			this._products$.next(res.items);
			this._productCount$.next(res.total);

		});
	}

	/**
	 * Handle item update
	 * @param item : Updated data object
	 * @returns
	 */
	updateMyProduct(item, id: number): Observable<UpdateProductModel> {
		const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.BUYER.PRODUCTS.UPDATE + `/${id}`;
		return this.put(url, item);
	}

	/**
	 * Find product details
	 */
	getProductDetails(productId: string) {
		const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.BUYER.PRODUCTS.SEARCH
			+ `/${productId}` + this.API_ROUTES.COMMON.DETAILS + `/${productId}?detailsType=product`;
		return this.getById(url, productId);
	}

	getImportStatus() {
		const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.BUYER.PRODUCTS.SEARCH
			+ this.API_ROUTES.BUYER.PRODUCTS.IMPORT_STATUS;
		return this.http.get(url, {responseType:'text', observe: 'response'});
	}

	retryImportProducts() {
		const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.BUYER.PRODUCTS.SEARCH
			+  this.API_ROUTES.BUYER.PRODUCTS.RETRY_IMPORT;
		return this.http.get(url);
	}

	/**
	 * Find product details
	 */
	getProductDetailsForTender(tenderId: string, productId: string): Observable<any> {
		const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.BUYER.PRODUCTS.SEARCH
			+ `/${productId}` + this.API_ROUTES.COMMON.DETAILS + `/${tenderId}?detailsType=tender`;
		return this.getDetails(url);
	}

	/**
	 * Find shop product details
	 */
	getProductDetailsForShop(productId: string): Observable<any> {
		const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.BUYER.PRODUCTS.SEARCH
			+ `/${productId}` + this.API_ROUTES.COMMON.DETAILS + `/${productId}?detailsType=shop`;
		return this.getDetails(url);
	}

	/**
	 * Download list of items
	 */
	downloadMyProducts(fileType: string, params) {
		const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.BUYER.PRODUCTS.DOWNLOAD;
		this._isLoading$.next(true);
		return this.downloadItems(url + `&fileType=${fileType}`, params, this.currentLanguage);
	}

	/**
	 * Download all categories
	 */
	downloadAllCategories() {
		const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.BUYER.CATEGORY.DOWNLOAD;
		this._isLoading$.next(true);
		return this.downloadItems(url, null , this.currentLanguage);
	}

	/**
	 * Delete list of items by ids
	 */
	deleteMyProducts(type: string, ids: number[]): Observable<any> {
		let url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.BUYER.PRODUCTS.DELETE;
		let body;

		if (type === DELETE_TYPE.ALL) {
			body = { deleteAll: true };
			return this.delete(url, body);
		}
		body = { deleteAll: false, productIds: ids };
		return this.delete(url, body);
	}

	/**
	 * Get all categories
	 */
	fetchCategories(tableState: ITableState): Observable<any> {
		const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.BUYER.CATEGORY.SEARCH;
		return this.findItemCategories(url, tableState, this.currentLanguage);
	}

	/**
	 *	Find by search string
	 * @param tableState :table state parameters
	 * @returns
	 */
	findAllProducts(tableState: ITableState): Observable<any> {
		const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.BUYER.PRODUCTS.SEARCH_SHOP;
		return this.findItems(url, tableState);
	}

	/**
	 *	Handles file validation functionality
	 * @param data : file to validate
	 */
	validateProductsFile(fileData,isExcel): Observable<any> {
		const api = this.API_URL + this.VERSION_V1 + this.API_ROUTES.BUYER.PRODUCTS.VALIDATE;
		const url = isExcel ? api + '?isExcel=true' : api;
		return this.create(fileData, url, this.currentLanguage);
	}

	/**
	 *	Handles file upload functionality
	 * @param data : file to upload
	 */
	uploadProducts(fileData,isExcel): Observable<any> {
		const api = this.API_URL + this.VERSION_V1 + this.API_ROUTES.BUYER.PRODUCTS.UPLOAD;
		const url = isExcel ? api + '?isExcel=true' : api;
		return this.create(fileData, url, this.currentLanguage);
	}

	/**
	 *	Handles product upload file validation functionality
	 * @param data : file to validate
	 */
	validateImportProductsFile(fileData): Observable<any> {
		const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.BUYER.TENDERS.PRODUCT_UPLOAD;
		return this.http.post<Observable<any>>(url, fileData, {
			reportProgress: true,
			observe: "events"
		  });
	}

	/**
	 * request for price
	 * for zero price product
	 */
	requestForPrice(productId: string) {
		const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.BUYER.PRODUCTS.SEARCH + '/' + productId
			+ this.API_ROUTES.BUYER.PRODUCTS.REQUEST_PRICE
		return this.http.get(url);
	}

	/**
	 * Get all contract suppliers
	 */
	fetchContractSuppliers(body): Observable<any> {
		const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.BUYER.CONTRACT.GET_CONTRACTED_SUPPLIERS;
		return this.findItemCategories(url, body);
	}

	/**
	 * Download list of items
	 */
	downloadTendersProducts(id: string, fileType: string) {
		const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.BUYER.TENDERS_PRODUCTS.EXPORT;
		this._isLoading$.next(true);
		return this.downloadItems(url + `/${id}/product/export?fileType=${fileType}`, null, this.currentLanguage);
	}

	// /**
	//  *	Find by search string
	//  * @param tableState :table state parametersSEARCH
	//  * @returns
	//  */
	// findProducts(tableState: ITableState): Observable<TableResponseModel<ProductsModel>> {
	// 	const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.BUYER.PRODUCTS.SEARCH;
	// 	return this.find(url);
	// }

	getResizeImage(fileName, width,height,isProfile){
		let url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.CORE.RESIZE_IMAGE.GET +
			`?height=${height}&width=${width}&src=${encodeURIComponent(fileName)}`;
		if(isProfile) {
			url = url.concat(`&isProfile=${isProfile}`);
		}
		return this.http.get(url,{ responseType: 'blob' });
	}

	fetchUploadLimit() {
		const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.BUYER.PRODUCTS.UPLOADS_LEFT;
		this.http.get<{uploadsLeft: number}>(url).subscribe((res) => {
			this._uploadsLeft$.next(res.uploadsLeft);
		});
	}

	uploadLimitReached() {
		return this._uploadsLeft$.getValue() <= 0;
	}

	findProductById(id: string): Observable<{ uploadsLeft: number }> {
		const url = `${this.API_URL}${this.VERSION_V1}${this.API_ROUTES.BUYER.PRODUCTS.SEARCH_PRODUCT}/${id}`;
		return this.http.get<{ uploadsLeft: number }>(url);
	}

	/**
	 *	Handles product AI parse file functionality
	 * @param data : file to validate
	 */
	validateAiParseProductFile(fileData): Observable<any> {
		const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.BUYER.TENDERS.PRODUCT_AI_PARSE;
		return this.http.post<Observable<any>>(url, fileData, {
			reportProgress: true,
			observe: "events"
		});
	}

}
